/**
 * Hooks React para permissões baseadas em perfil
 * 
 * Fornece hooks para verificar permissões no frontend baseado no perfil do usuário.
 * NOVA VERSÃO: Usa isOperationalSquad ao invés de isFinancial, remove super_admin
 */

import { useMemo } from "react";
import { useSession } from "next-auth/react";
import { useCurrentCompany } from "@/hooks/api/use-companies";
import type { Session } from "next-auth";

/**
 * Tipos de perfil do usuário
 */
type UserProfileType = 
  | "master_operational"    // Master em Squad Operacional (acesso total)
  | "master_regular"        // Master em squad normal (acesso limitado)
  | "digitador"
  | "other";

/**
 * Interface para representar o perfil do usuário no frontend
 */
interface UserProfile {
  type: UserProfileType;
  isMaster: boolean;
  isOperationalSquad: boolean;  // NOVO: substitui isFinancial
  isDigitador: boolean;
  isAdminInterno: boolean;
  userSquads: string[];
  companyId: string | null;
  userId: string | null;
}

/**
 * Obtém o perfil do usuário a partir da sessão
 */
function getUserProfileFromSession(session: Session | null, companyId: string | null): UserProfile {
  if (!session?.user) {
    return {
      type: "other",
      isMaster: false,
      isOperationalSquad: false,
      isDigitador: false,
      isAdminInterno: false,
      userSquads: [],
      companyId: null,
      userId: null,
    };
  }

  const userRoles = session.user.roles ?? [];
  const isAdminInterno = userRoles.includes("admin_interno");
  const isMaster = userRoles.includes("master");
  const isDigitador = userRoles.includes("digitador");
  const isOperationalSquad = session.user.isOperationalSquad ?? false;
  const userSquads = (session.user.squads ?? []) as string[];

  let profileType: UserProfileType = "other";
  
  if (isMaster) {
    profileType = isOperationalSquad ? "master_operational" : "master_regular";
  } else if (isDigitador) {
    profileType = "digitador";
  }

  return {
    type: profileType,
    isMaster,
    isOperationalSquad,
    isDigitador,
    isAdminInterno,
    userSquads,
    companyId,
    userId: session.user.id,
  };
}

/**
 * Hook para obter o perfil do usuário atual
 */
export function useUserProfile(): UserProfile {
  const { data: session, status: sessionStatus } = useSession();
  const { data: company } = useCurrentCompany();
  
  return useMemo(() => {
    // Se a sessão ainda está carregando, retorna perfil vazio
    if (sessionStatus === "loading") {
      return {
        type: "other",
        isMaster: false,
        isOperationalSquad: false,
        isDigitador: false,
        isAdminInterno: false,
        userSquads: [],
        companyId: null,
        userId: null,
      };
    }
    return getUserProfileFromSession(session, company?.id ?? null);
  }, [session, sessionStatus, company]);
}

/**
 * Hook para verificar se o usuário pode visualizar a tela de hierarquia
 * 
 * Regras:
 * - Digitador: NÃO pode ver
 * - Demais: Podem ver
 */
export function useCanViewHierarchy(): boolean {
  const profile = useUserProfile();
  
  return useMemo(() => {
    // Digitador não pode ver a hierarquia
    if (profile.isDigitador) {
      return false;
    }
    
    return true;
  }, [profile.isDigitador]);
}

/**
 * Hook para verificar se o usuário pode adicionar uma nova empresa
 * 
 * Regras:
 * - Apenas empresa TopaMais (isMaster=true) pode adicionar empresas
 */
export function useCanAddCompany(): boolean {
  const { data: company } = useCurrentCompany();
  
  return useMemo(() => {
    return company?.isMaster === true;
  }, [company?.isMaster]);
}

/**
 * Hook para verificar se o usuário pode ver todos os parceiros
 * 
 * Regras:
 * - TopaMais: Vê TODOS os parceiros
 * - Demais: Só veem os seus respectivos
 */
export function useCanViewAllPartners(): boolean {
  const { data: company } = useCurrentCompany();
  
  return useMemo(() => {
    return company?.isMaster === true;
  }, [company?.isMaster]);
}

/**
 * Hook para verificar se o usuário pode adicionar um usuário
 * 
 * Regras:
 * - Master em Squad Operacional: Pode adicionar usuários à empresa (parceiro)
 * - Master em squad normal: Pode adicionar usuários somente na equipe dele
 * - Digitador: Não pode adicionar usuários
 * - Admin Interno: Podem adicionar
 */
export function useCanAddUser(targetSquadId?: string): boolean {
  const profile = useUserProfile();
  
  return useMemo(() => {
    // Admin Interno podem sempre adicionar
    if (profile.isAdminInterno) {
      return true;
    }
    
    // Digitador não pode adicionar usuários
    if (profile.isDigitador) {
      return false;
    }
    
    // Master em Squad Operacional: pode adicionar usuários à empresa
    if (profile.type === "master_operational") {
      return true;
    }
    
    // Master em squad normal: pode adicionar usuários somente na equipe dele
    if (profile.type === "master_regular") {
      // Deve ter squads
      if (profile.userSquads.length === 0) {
        return false;
      }
      
      // Se targetSquadId foi fornecido, verificar se está nas equipes do usuário
      if (targetSquadId) {
        return profile.userSquads.includes(targetSquadId);
      }
      
      // Se não foi fornecido, permitir (será validado no momento da criação)
      return true;
    }
    
    return false;
  }, [profile, targetSquadId]);
}

/**
 * Hook para verificar se o usuário pode editar um usuário específico
 * 
 * Nota: Esta verificação completa requer dados do servidor.
 * Este hook faz uma verificação básica no frontend.
 * A validação completa deve ser feita no backend.
 */
export function useCanEditUser(targetUserId?: string): boolean {
  const profile = useUserProfile();
  
  return useMemo(() => {
    // Admin Interno podem sempre editar
    if (profile.isAdminInterno) {
      return true;
    }
    
    // Digitador não pode editar outros usuários
    if (profile.isDigitador) {
      // Só pode editar a si mesmo
      return targetUserId === profile.userId;
    }
    
    // Se for o próprio usuário, pode editar
    if (targetUserId === profile.userId) {
      return true;
    }
    
    // Master em Squad Operacional: pode editar qualquer usuário da empresa
    if (profile.type === "master_operational") {
      return true;
    }
    
    // Master em squad normal: verificação básica (validação completa no backend)
    if (profile.type === "master_regular") {
      return profile.userSquads.length > 0;
    }
    
    return false;
  }, [profile, targetUserId]);
}

/**
 * Hook para verificar se o usuário pode visualizar relatórios financeiros
 * 
 * Regras:
 * - Master em Squad Operacional: Pode ver (incluindo relatório de comissão)
 * - Master em squad normal: Não pode ver
 * - Digitador: Não pode ver
 * - Admin Interno: Podem ver
 */
export function useCanViewFinancialReports(): boolean {
  const profile = useUserProfile();
  
  return useMemo(() => {
    // Admin Interno podem sempre ver
    if (profile.isAdminInterno) {
      return true;
    }
    
    // Apenas Master em Squad Operacional pode ver relatórios financeiros
    return profile.type === "master_operational";
  }, [profile]);
}

/**
 * Hook para verificar se o usuário pode alterar a senha de um usuário
 * 
 * Regras:
 * - Digitador: Só pode alterar a própria senha
 * - Demais: Podem alterar conforme regras de edição de usuário
 */
export function useCanChangePassword(targetUserId?: string): boolean {
  const profile = useUserProfile();
  const canEdit = useCanEditUser(targetUserId);
  
  return useMemo(() => {
    // Digitador: só pode alterar a própria senha
    if (profile.isDigitador) {
      return targetUserId === profile.userId;
    }
    
    // Para outros perfis, segue as regras de edição de usuário
    return canEdit;
  }, [profile, targetUserId, canEdit]);
}

/**
 * Hook para verificar se o usuário pode visualizar simulações
 * 
 * Regras:
 * - Todos os perfis podem visualizar simulações
 */
export function useCanViewSimulations(): boolean {
  return true;
}

/**
 * Hook para verificar se o usuário pode visualizar uma proposta específica
 * 
 * Regras:
 * - Admin Interno: sempre podem visualizar
 * - Master em Squad Operacional: pode visualizar qualquer proposta da empresa
 * - Master em squad normal: pode visualizar propostas do seu time e times vinculados
 * - Digitador: pode visualizar propostas dele
 */
export function useCanViewProposal(
  proposalSquadId?: string | null,
  proposalTypedBy?: string | null
): boolean {
  const profile = useUserProfile();
  
  return useMemo(() => {
    // Admin Interno podem sempre visualizar
    if (profile.isAdminInterno) {
      return true;
    }
    
    // Master em Squad Operacional: pode visualizar qualquer proposta da empresa
    if (profile.type === "master_operational") {
      return true;
    }
    
    // Master em squad normal: verificação básica (validação completa no backend)
    // No frontend, verificamos se o squad está nos squads do usuário
    // O backend fará a verificação completa incluindo hierarquia de squads filhos
    if (profile.type === "master_regular") {
      if (!proposalSquadId) {
        // Se a proposta não tem squad, pode visualizar se for o criador
        return proposalTypedBy === profile.userId;
      }
      
      // Verificação básica: se o squad está nos squads diretos do usuário
      // O backend verificará se está na hierarquia completa (incluindo filhos)
      return profile.userSquads.includes(proposalSquadId);
    }
    
    // Digitador: só pode visualizar propostas dele
    if (profile.isDigitador) {
      if (!proposalTypedBy || !profile.userId) {
        return false;
      }
      return proposalTypedBy === profile.userId;
    }
    
    return false;
  }, [profile, proposalSquadId, proposalTypedBy]);
}

/**
 * Hook para verificar se o usuário pode editar uma proposta específica
 * 
 * Regra: Se pode VER, pode EDITAR.
 * 
 * Nota: Esta é uma verificação básica no frontend. A validação completa (incluindo
 * hierarquia completa de squads) é feita no backend através de canEditProposal.
 * O frontend é mais permissivo para melhorar UX, mas o backend garante a segurança.
 */
export function useCanEditProposal(
  proposalSquadId?: string | null,
  proposalTypedBy?: string | null
): boolean {
  // Se pode ver, pode editar
  return useCanViewProposal(proposalSquadId, proposalTypedBy);
}
